import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Alert,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import classNames from "classnames";
import FormInput from "../../../../../../toolkit/formInput";
import WizardFormStep from "../../components/WizardFormStep";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import * as yup from "yup";
import withStyles from "isomorphic-style-loader/withStyles";
import orgStyles from "../../organizationSetup/styles.scss";
import donorStyles from "../../../../donors/styles.scss";
import s from "./styles.scss";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { useLoading } from "../../../../../../hooks/useLoading";
import { MmsService } from "../../../../../../services/mmsService";
import { AlertColor } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { saveMmsSetupData } from "../../../../../../utils/firebase/mmsSetup";
import {
  getMmsSharedTcrCampaigns,
  MmsSharedTcrOption,
  syncMmsSenderAreaCode,
} from "../../../../../../utils/mmsOnboardingApi";
import { bandwidthAreaCodeField } from "../../../../../../utils/bandwidthAreaCodeValidation";
import { formatMmsApiErrorMessage } from "../../../../../../utils/mmsApiErrorMessage";
import { canUseSharedTcrOption } from "../../../../../../utils/onboarding/onboardingAccess";
import { connect } from "react-redux";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export const areaCodeInputSchema = yup.object({
  areaCode: bandwidthAreaCodeField,
  useSharedTcr: yup.boolean(),
  sharedTcrEntryId: yup.string().when("useSharedTcr", {
    is: true,
    then: (schema) => schema.required("Please select a shared TCR campaign"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

interface AreaCodeInputProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  onConfigurationSaved?: () => void | Promise<void>;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
  isSuperadmin?: boolean;
  isOnboardingOperator?: boolean;
}

const AreaCodeInput: React.FC<AreaCodeInputProps> = ({
  charityId,
  config,
  serviceAccessToken,
  onConfigurationSaved,
  showSnackbar,
  isSuperadmin = false,
  isOnboardingOperator = false,
}) => {
  const { setValue, watch, clearErrors } = useFormContext();
  const areaCode = watch("areaCode");
  const useSharedTcr = watch("useSharedTcr");
  const sharedTcrEntryId = watch("sharedTcrEntryId");
  const { loading, runWithLoading } = useLoading();
  const [sharedTcrOptions, setSharedTcrOptions] = useState<MmsSharedTcrOption[]>([]);
  const [sharedTcrDropdownOpen, setSharedTcrDropdownOpen] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const readyToSubmit = availabilityChecked && isAvailable;
  const showSharedTcrOption = canUseSharedTcrOption({
    isSuperadmin,
    isOnboardingOperator,
  });

  useEffect(() => {
    setAvailabilityChecked(false);
    setIsAvailable(false);
    setErrorMessage(null);
  }, [areaCode]);

  useEffect(() => {
    if (!showSharedTcrOption || !config || !serviceAccessToken || !charityId) {
      return;
    }
    getMmsSharedTcrCampaigns(config.adminServiceUrl!, charityId, serviceAccessToken)
      .then((options) => setSharedTcrOptions(options || []))
      .catch(() => setSharedTcrOptions([]));
  }, [showSharedTcrOption, config, serviceAccessToken, charityId]);

  const handleSharedTcrToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setValue("useSharedTcr", checked);
    if (!checked) {
      setValue("sharedTcrEntryId", "");
      clearErrors("sharedTcrEntryId");
      setSharedTcrDropdownOpen(false);
    }
  };

  const handleSharedTcrSelect = (entryId: string) => {
    setValue("sharedTcrEntryId", entryId);
    clearErrors("sharedTcrEntryId");
    setSharedTcrDropdownOpen(false);
  };

  const selectedSharedTcrLabel =
    sharedTcrOptions.find((option) => option.entryId === sharedTcrEntryId)?.label ||
    "Shared TCR campaign...";

  const handleCheckAvailability = async (formData: { areaCode: string }) => {
    const areaCode = formData.areaCode;

    setErrorMessage(null);
    setAvailabilityChecked(false);
    setIsAvailable(false);

    await runWithLoading(async () => {
      try {
        if (!config || !serviceAccessToken || !charityId) {
          throw new Error("Missing configuration. Please try again.");
        }

        const response = await MmsService.checkAreaCodeAvailability(
          config,
          areaCode,
          charityId,
          serviceAccessToken
        );

        if (response.available) {
          setIsAvailable(true);
          setAvailabilityChecked(true);
          setErrorMessage(null);

          if (showSnackbar) {
            showSnackbar(
              `Numbers are available in area code ${areaCode}. Click Submit to save your configuration.`,
              "success"
            );
          }
        } else {
          setAvailabilityChecked(true);
          setIsAvailable(false);
          const message =
            response.message ||
            `No numbers available in area code ${areaCode}. Please try a different area code.`;
          setErrorMessage(message);
          throw new Error(message);
        }
      } catch (error) {
        setIsAvailable(false);
        setAvailabilityChecked(true);
        const errorMsg = formatMmsApiErrorMessage(
          error,
          "Failed to check area code availability. Please try again."
        );
        setErrorMessage(errorMsg);
        throw new Error(errorMsg);
      }
    }, 800);
  };

  const handleSubmit = async (formData: {
    areaCode: string;
    useSharedTcr?: boolean;
    sharedTcrEntryId?: string;
  }) => {
    const areaCode = formData.areaCode;

    setErrorMessage(null);

    await runWithLoading(async () => {
      try {
        if (!config || !serviceAccessToken || !charityId) {
          throw new Error("Missing configuration. Please try again.");
        }

        const saveData: Parameters<typeof saveMmsSetupData>[1] = {
          mmsSenderAreaCode: areaCode,
          phoneNumberMethod: "newNumber",
          status: "pending",
          configuredAt: Date.now(),
        };

        if (formData.useSharedTcr && formData.sharedTcrEntryId) {
          const selected = sharedTcrOptions.find(
            (option) => option.entryId === formData.sharedTcrEntryId
          );
          if (selected) {
            saveData.useSharedTcr = true;
            saveData.sharedTcr = {
              entryId: selected.entryId,
              label: selected.label,
            };
          }
        }

        await saveMmsSetupData(charityId, saveData);
        await syncMmsSenderAreaCode(
          config.adminServiceUrl!,
          charityId,
          serviceAccessToken,
          areaCode
        );

        if (onConfigurationSaved) {
          await onConfigurationSaved();
        }
      } catch (error) {
        setIsAvailable(false);
        setAvailabilityChecked(false);
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Failed to save MMS configuration. Please try again.";
        setErrorMessage(errorMsg);
        throw error;
      }
    }, 800);
  };

  const handleFinish = async (formData: {
    areaCode: string;
    useSharedTcr?: boolean;
    sharedTcrEntryId?: string;
  }) => {
    if (readyToSubmit) {
      await handleSubmit(formData);
    } else {
      await handleCheckAvailability(formData);
    }
  };

  return (
    <WizardFormStep
      title="Enter Area Code"
      subtitle="We'll find an available phone number in your area"
    >
      <Row className="mt-4 justify-content-center">
        <Col md="8">
          <FormInput
            name="areaCode"
            type="text"
            icon="fa-solid fa-phone"
            placeholder="Area Code (e.g., 555)"
            maxLength={3}
          />

          <p className={orgStyles.helperText}>
            We'll automatically purchase a number with this area code for your organization.
          </p>

          {showSharedTcrOption && sharedTcrOptions.length > 0 && (
            <div className={s.sharedTcrSection}>
              <div className={s.sharedTcrCheckbox}>
                <Input
                  type="checkbox"
                  id="useSharedTcr"
                  checked={Boolean(useSharedTcr)}
                  onChange={handleSharedTcrToggle}
                />
                <label htmlFor="useSharedTcr" />
                <span className={s.sharedTcrCheckboxLabel}>
                  Use an already existing TCR brand and campaign
                </span>
              </div>
              {useSharedTcr && (
                <div className={s.sharedTcrDropdownWrap}>
                <Dropdown
                  className={donorStyles.bulkActionsDropdown}
                  isOpen={sharedTcrDropdownOpen}
                  toggle={() => setSharedTcrDropdownOpen((open) => !open)}
                >
                  <DropdownToggle
                    caret
                    className={classNames(
                      donorStyles.button,
                      donorStyles.bulkActionsDropdownToggle
                    )}
                    color="none"
                    id="sharedTcrCampaignDropdown"
                    aria-label="Shared TCR campaign"
                  >
                    {selectedSharedTcrLabel}
                  </DropdownToggle>
                  <DropdownMenu
                    end
                    className={classNames(
                      donorStyles.contactsDropdownMenu,
                      donorStyles.bulkActionsDropdownMenu
                    )}
                  >
                    {sharedTcrOptions.map((option) => (
                      <DropdownItem
                        key={option.entryId}
                        toggle={false}
                        active={option.entryId === sharedTcrEntryId}
                        onClick={() => handleSharedTcrSelect(option.entryId)}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
                </div>
              )}
            </div>
          )}

          {availabilityChecked && isAvailable && (
            <Alert color="success" fade={false} className={s.successAlert}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <CheckCircleIcon className="me-2" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <strong>Great!</strong> Numbers are available. Click Submit to save your configuration.
                </div>
              </div>
            </Alert>
          )}

          {errorMessage && (
            <Alert color="danger" fade={false} className={s.dangerAlert}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <ErrorIcon className="me-2" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <strong>Not Available:</strong> {errorMessage}
                </div>
              </div>
            </Alert>
          )}
        </Col>
      </Row>

      <WizardFooter
        schema={areaCodeInputSchema}
        validationFields={readyToSubmit ? undefined : ["areaCode"]}
        onFinish={handleFinish}
        finishButtonText={readyToSubmit ? "Submit" : "Check Availability"}
        loading={loading}
        loadingText={readyToSubmit ? "Submitting..." : "Checking availability..."}
      />
    </WizardFormStep>
  );
};

const mapStateToProps = (state: { user: { isSuperadmin: boolean; isOnboardingOperator: boolean } }) => ({
  isSuperadmin: state.user.isSuperadmin,
  isOnboardingOperator: state.user.isOnboardingOperator,
});

export default withStyles(s, orgStyles, donorStyles)(
  connect(mapStateToProps)(AreaCodeInput)
);
